import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  Brand,
  FeeSchedule,
  Organisation,
} from '@principle-theorem/principle-core';
import { PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE } from '@principle-theorem/principle-core/features';
import {
  type IBrand,
  type IFeeSchedule,
  type IOrganisation,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  type WithRef,
  Firestore,
  multiSwitchMap,
  reduce2DArray,
  shareReplayCold,
} from '@principle-theorem/shared';
import { type Observable, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

export interface IFeeScheduleCleanupDialogData {
  organisation: WithRef<IOrganisation>;
}

interface IFeeScheduleRow {
  feeSchedule: WithRef<IFeeSchedule>;
  parentName: string;
  parentType: 'Practice' | 'Staffer';
  serviceCodeCount: number;
  treatmentFeeCount: number;
  isDeleted: boolean;
}

@Component({
  selector: 'pt-fee-schedule-cleanup-dialog',
  templateUrl: './fee-schedule-cleanup-dialog.component.html',
  styleUrls: ['./fee-schedule-cleanup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
  ],
})
export class FeeScheduleCleanupDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<FeeScheduleCleanupDialogComponent>>(MatDialogRef);
  private _snackBar = inject(MatSnackBar);
  data = inject<IFeeScheduleCleanupDialogData>(MAT_DIALOG_DATA);

  deleting = signal<string | undefined>(undefined);
  restoring = signal<string | undefined>(undefined);
  featureEnabled = signal(
    (this.data.organisation.optInFeatures ?? []).includes(
      PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE.label
    )
  );

  displayedColumns = [
    'parentType',
    'parentName',
    'name',
    'serviceCodes',
    'treatmentFees',
    'status',
    'actions',
  ];

  feeScheduleRows$: Observable<IFeeScheduleRow[]>;
  loading$: Observable<boolean>;

  constructor() {
    const org = this.data.organisation;

    const brands$ = Organisation.brands$(org);

    const practiceSchedules$ = brands$.pipe(
      multiSwitchMap((brand: WithRef<IBrand>) => Brand.practices$(brand)),
      reduce2DArray(),
      switchMap((practices: WithRef<IPractice>[]) =>
        practices.length === 0
          ? of([])
          : combineLatest(
              practices.map((practice) =>
                this._getFeeScheduleRows(practice, practice.name, 'Practice')
              )
            ).pipe(reduce2DArray())
      ),
      shareReplayCold()
    );

    const stafferSchedules$ = brands$.pipe(
      multiSwitchMap((brand: WithRef<IBrand>) => Brand.staff$(brand)),
      reduce2DArray(),
      switchMap((staffers: WithRef<IStaffer>[]) =>
        staffers.length === 0
          ? of([])
          : combineLatest(
              staffers.map((staffer) =>
                this._getFeeScheduleRows(staffer, staffer.user.name, 'Staffer')
              )
            ).pipe(reduce2DArray())
      ),
      shareReplayCold()
    );

    this.feeScheduleRows$ = combineLatest([
      practiceSchedules$,
      stafferSchedules$,
    ]).pipe(
      map(([practice, staffer]) => [...practice, ...staffer]),
      shareReplayCold()
    );

    this.loading$ = this.feeScheduleRows$.pipe(
      map(() => false),
      startWith(true),
      shareReplayCold()
    );
  }

  async deleteFeeSchedule(row: IFeeScheduleRow): Promise<void> {
    const ref = row.feeSchedule.ref;
    this.deleting.set(ref.path);
    try {
      await Firestore.patchDoc(ref, { deleted: true });
      this._snackBar.open(
        `Deleted fee schedule "${row.feeSchedule.name}" from ${row.parentName}`,
        'OK',
        { duration: 3000 }
      );
    } catch {
      this._snackBar.open('Failed to delete fee schedule', 'OK', {
        duration: 5000,
      });
    } finally {
      this.deleting.set(undefined);
    }
  }

  async restoreFeeSchedule(row: IFeeScheduleRow): Promise<void> {
    const ref = row.feeSchedule.ref;
    this.restoring.set(ref.path);
    try {
      await Firestore.patchDoc(ref, { deleted: false });
      this._snackBar.open(
        `Restored fee schedule "${row.feeSchedule.name}" for ${row.parentName}`,
        'OK',
        { duration: 3000 }
      );
    } catch {
      this._snackBar.open('Failed to restore fee schedule', 'OK', {
        duration: 5000,
      });
    } finally {
      this.restoring.set(undefined);
    }
  }

  async toggleFeatureFlag(): Promise<void> {
    const org = this.data.organisation;
    const label = PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE.label;
    const isEnabled = this.featureEnabled();

    const currentFeatures = org.optInFeatures ?? [];
    const updatedFeatures = isEnabled
      ? currentFeatures.filter((f) => f !== label)
      : [...currentFeatures, label];

    try {
      await Firestore.patchDoc(org.ref, {
        optInFeatures: updatedFeatures,
      });
      org.optInFeatures = updatedFeatures;
      this.featureEnabled.set(!isEnabled);
      this._snackBar.open(
        `Practice & staffer fee schedules ${
          !isEnabled ? 'enabled' : 'disabled'
        }`,
        'OK',
        { duration: 3000 }
      );
    } catch {
      this._snackBar.open('Failed to toggle feature flag', 'OK', {
        duration: 5000,
      });
    }
  }

  close(): void {
    this._dialogRef.close();
  }

  private _getFeeScheduleRows(
    parent: WithRef<IPractice> | WithRef<IStaffer>,
    parentName: string,
    parentType: 'Practice' | 'Staffer'
  ): Observable<IFeeScheduleRow[]> {
    return Firestore.query$(FeeSchedule.col(parent)).pipe(
      map((schedules) =>
        schedules.map((feeSchedule) => ({
          feeSchedule,
          parentName,
          parentType,
          serviceCodeCount: feeSchedule.serviceCodes?.length ?? 0,
          treatmentFeeCount: feeSchedule.treatmentFees?.length ?? 0,
          isDeleted: feeSchedule.deleted === true,
        }))
      )
    );
  }
}
